import { z } from "zod";
import { CandidateCountSchema, SimulationObjectiveSchema } from "./simulations-enums.js";
import type { ConfidenceBasisKey, TrackRecordSourceKey } from "./track-record.js";

// Os enums moram no módulo folha (sem ciclo com o módulo do lote). Re-exportados um a
// um, e NÃO em bloco: `ConfidenceLevelSchema` já é exportado por outro contrato do barrel, e o
// re-export amplo criaria ambiguidade.
export {
  AnySimulationObjectiveSchema,
  CandidateCountSchema,
  SimulationBatchKindSchema,
  SimulationBatchStatusSchema,
  SimulationObjectiveSchema,
} from "./simulations-enums.js";
export type {
  AnySimulationObjective,
  SimulationBatchKind,
  SimulationBatchStatus,
  SimulationObjective,
} from "./simulations-enums.js";

export const ExecuteSimulationRequestSchema = z.object({
  productId: z.string().uuid(),
  horizonHours: z.number().positive().max(720).default(24),
});
export type ExecuteSimulationRequest = z.infer<typeof ExecuteSimulationRequestSchema>;

export const CompareQuerySchema = z.object({ a: z.string().uuid(), b: z.string().uuid() });

// Execução de cenário estrutural (período/OP + gate de compatibilidade):
// schemas em ./simulations-run.ts (mantém este arquivo ≤200 LOC), re-exportados no index.

// Gargalo estruturado (menor vazão efetiva = capacidade × disponibilidade).
// Pode listar >1 máquina em empate. `note` declara a limitação do modelo (processos
// independentes, sem fila acoplada).
export interface SimulationBottleneck {
  machineIds: string[];
  machineNames: string[];
  effectiveThroughputUnH: number;
  note: string;
}

export interface SimulationPerMachine {
  machineId: string;
  name: string;
  effectiveThroughputUnH: number;
  availability: number; // 0..1
  units: number;
}

// Premissa estruturada por máquina/parâmetro. `origin` distingue medido (Spec 2) de
// premissa (1a); campos opcionais mantêm compatibilidade com simulações legadas.
export interface SimulationDataSource {
  component: "availability" | "performance" | "quality";
  source: "sensor" | "integration" | "import" | "calculated" | "manual";
  machineId?: string;
  parameterName?: string;
  origin?: "measured" | "assumed";
  // Param sem dado informado (fallback conservador) → conta como "sem histórico" na confiança.
  defaulted?: boolean;
}

export interface SimulationResult {
  id: string;
  scenarioId: string;
  // Null em rodadas de cenário "linha nova" (sem linha base).
  lineId: string | null;
  status: "DRAFT" | "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";
  parameters: Record<string, unknown>;
  results: {
    availability: number;
    // ANULÁVEIS. Sem plano declarado não existe denominador para o Desempenho,
    // e reportar zero mentiria (zero é desempenho péssimo; ausência de plano não é). É o espelho
    // exato da guarda que o `calculate_oee` aplica há muito, e o que torna os dois comparáveis.
    // ⚠️ Todo consumidor renderiza "—" quando nulo, NUNCA `0`. `results.oee.toFixed(1)` cru
    // explode em runtime; o typecheck pega os do web, mas NÃO os da API — ver
    // `assistant/projections-simulation.ts`.
    // `availability` e `quality` seguem obrigatórios: expediente é sempre positivo e qualidade é
    // razão de contagens. Só duas das quatro mudam de tipo.
    performance: number | null;
    quality: number;
    oee: number | null;
    estimatedProduction: number;
    diagnostic: string;
    bottleneck?: SimulationBottleneck;
    perMachine?: SimulationPerMachine[];
    // Vazão por estágio de fluxo (série=min / paralelo=soma).
    perStage?: Array<{ stageIndex: number; machineIds: string[]; throughputUnH: number }>;
    // Meta de quantidade. TODOS opcionais: rodadas antigas não os têm e
    // precisam continuar legíveis.
    // `timeToTargetS` está no EIXO OPERACIONAL (o motor colapsa o tempo quando há expediente —
    // des.py:680-683), nunca no relógio civil. A conversão é do TS, via toWallClockSeconds, usando
    // as janelas congeladas na execução — converter com o calendário de hoje desloca em silêncio.
    timeToTargetS?: number | null;
    // A MAIORIA das réplicas cruzou o alvo dentro do teto. `false` significa "não atingiu no
    // horizonte avaliado", JAMAIS "impossível".
    targetReached?: boolean;
    // Fração de réplicas que cruzaram (0..1). A média de `timeToTargetS` considera só as que
    // cruzaram, logo é otimista por construção: este campo é o que torna o viés visível.
    targetReachedRatio?: number;
    // Instante interpolado em vez de observado — regime SEM arestas, que não tem evento por peça
    // (des.py:81). Linha de estágio único cai aqui; a UI rotula como estimativa.
    targetEstimated?: boolean;
  } | null;
  dataSources: SimulationDataSource[] | null;
  confidenceLevel: "HIGH" | "MEDIUM" | "LOW" | "INSUFFICIENT" | null;
  // ── Spec 079 (FR-015/FR-018) — o nível EXIBIDO e de onde ele veio ──────────────────────────
  // OPCIONAIS de propósito: execução anterior a esta feature não os tem, e o consumidor cai em
  // `confidenceLevel` — que NÃO muda de sentido (continua o nível por PROCEDÊNCIA, agora a
  // entrada da resolução em vez da resposta final). São campos IRMÃOS dele, jamais valores
  // extras do enum de confiança: empacotar "veio do histórico" como um quinto nível quebraria
  // todo consumidor que casa contra os quatro.
  displayedConfidenceLevel?: "HIGH" | "MEDIUM" | "LOW" | "INSUFFICIENT" | null;
  confidenceBasis?: ConfidenceBasisKey | null;
  trackRecordSource?: TrackRecordSourceKey | null;
  // A versão do motor que PRODUZIU este resultado. Anulável apenas por causa
  // do histórico anterior à feature; execução nova sempre a carrega. Sem ela um snapshot
  // não é reproduzível, e reprodutibilidade é exigência do Princípio II.
  engineVersion: string | null;
  errorMessage: string | null;
  queuedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

// O preview leve síncrono (SimulationDataSourceSchema + PreviewResponseSchema)
// mora em ./simulations-preview.js (re-exportado pelo index).

// ── Simulação Automática (lote de candidatos) ─────────────────────────────────

// Cria um lote: o objetivo escolhido guia a recomendação e define qual dado é exigido.
// Base por CENÁRIO (`scenarioId`) OU por linha+produto, exclusivo.
// Duas mudanças de contrato:
//  1. `objective` PERDE o default. Simular sem objetivo deixou de fazer sentido: o
//     objetivo é o primeiro passo do fluxo, não um refinamento opcional.
//  2. A obrigatoriedade de `targetQuantity` mudou de dono — TARGET_QUANTITY agora responde
//     "quanto produzo na janela" e a meta virou OPCIONAL (sem meta ⇒ maximiza produção).
// Cada refine de obrigatório condicional ancora `path` NO CAMPO QUE FALTOU. Ancorar
// num campo presente faz `classifyArgIssues` classificar como `custom`, e o comando do assistente
// é descartado sem perguntar nada ao usuário (regressão já ocorrida duas vezes no repositório).
export const CreateAutoBatchRequestSchema = z
  .object({
    scenarioId: z.string().uuid().optional(),
    lineId: z.string().uuid().optional(),
    productId: z.string().uuid().optional(),
    objective: SimulationObjectiveSchema,
    targetQuantity: z.number().int().positive().optional(),
    // De qual OP a promessa de prazo é feita; exclusivo do objetivo OP_ON_TIME.
    productionOrderId: z.string().uuid().optional(),
    variationPct: z.number().int().min(1).max(25).default(10),
    candidateCount: CandidateCountSchema.default(6),
    horizonHours: z.number().int().positive().max(720).default(24),
  })
  // Cenário de "linha nova" (baseLineId=null) sugere só com scenarioId; a
  // exigência de linha base foi removida no service (auto-batch.base.ts), não aqui.
  .refine((v) => (v.scenarioId != null) !== (v.lineId != null && v.productId != null), {
    message: "Informe scenarioId OU lineId+productId (exclusivo).",
    path: ["scenarioId"],
  })
  .refine((v) => v.objective !== "TIME_TO_QUANTITY" || (v.targetQuantity ?? 0) > 0, {
    message: "Informe a quantidade a produzir.",
    path: ["targetQuantity"],
  })
  .refine((v) => v.objective !== "OP_ON_TIME" || v.productionOrderId != null, {
    message: "Informe a Ordem de Produção a avaliar.",
    path: ["productionOrderId"],
  })
  .refine((v) => v.objective === "OP_ON_TIME" || v.productionOrderId == null, {
    message: "Ordem de Produção só se aplica ao objetivo de entrega no prazo.",
    path: ["productionOrderId"],
  })
  .refine(
    (v) =>
      v.objective === "TARGET_QUANTITY" ||
      v.objective === "TIME_TO_QUANTITY" ||
      v.targetQuantity == null,
    {
      message: "Quantidade não se aplica ao objetivo escolhido.",
      path: ["targetQuantity"],
    }
  );
export type CreateAutoBatchRequest = z.infer<typeof CreateAutoBatchRequestSchema>;

// O lote (candidatos/cold-start/resposta) mora em./simulations-batch.js por causa do
// teto de 200 linhas; re-exportado aqui para os consumidores não precisarem saber da divisão.
export * from "./simulations-batch.js";

// Interno do módulo do lote (não vai ao barrel: `ConfidenceLevel` já é exportado por outro
// contrato, e dois nomes iguais no barrel dão ambiguidade).
